import { Button } from '@gisce/oficina-virtual-components';
import { ArrowBack, Refresh } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router";
import { FC } from 'react';
import authActions from "../Authentication/actions";
import { useDispatch } from 'react-redux';
import ErrorLayout from './components/ErrorLayout';
import { lastEventId } from '@sentry/react';

type ErrorCodes<T extends number> = T | (number & {});


/* Error code types
  401 -> Authentication error
  500 -> Generic anything could've happened
  503 -> Website is in maintenance
  504 -> Openid is active, code and state was provided but nonce was not found
*/
type HandledErrorCodes = ErrorCodes<401 | 500 | 503 | 504 | 505>

export type ErrorComponentPageProps = {
  status?: HandledErrorCodes;
};

const ErrorComponentPage: FC<ErrorComponentPageProps> = ({
  status = 500
}) => { 
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const getImageSrcFromErrorCode = (statusCode: number): string | undefined  => {
    switch (statusCode) {
      case 500: return "/images/error/crash/lightpole.jpg";
      case 503: return "/images/maintenance.jpg";
      default: return "/images/error/crash/lightpole.jpg";
    }
  }

  const getErrorTitleFromCode = (statusCode: number): string => {
    switch (statusCode) {
      case 401: return t('common:text.error.auth_page_title');
      case 500: return t('common:text.error.crash_view_title');
      case 503: return t('common:text.maintenance.view_title');
      case 504: return t('common:text.error.nonce_not_present_title');
      case 505: return t('common:text.error.clientid_incorrect_title');
      default: return t('common:text.error.page_title');
    }
  }

  const authResetNavigate = () => {
    navigate("/");
    navigate(0);
  }

  return <ErrorLayout
    imageSrc={getImageSrcFromErrorCode(status)}
    errorTitle={getErrorTitleFromCode(status)}
    errorDetailComponent={<>
      {status === 503 &&
        <Box
          component="img"
          display="flex"
          alignSelf="center"
          sx={{
            [theme.breakpoints.down("md")]: {
              marginTop: 3,
              marginBottom: 3,
            },
            [theme.breakpoints.up("md")]: {
              marginBottom: 6
            },
            width: "75%"
          }}
          src="/images/logo.png"
        />
      }
      <Typography variant="h6" pb={1}>
        {status && t('common:text.error.code_message', {"errorCode": status})}
      </Typography>
      <Typography variant="body1">
        {status === 500 && t('common:text.error.crash_view_message')}
        {status === 503 && t('common:text.maintenance.view_subtitle')}
        {status === 504 && t('common:text.error.nonce_not_present_explanation')}
        {status === 505 && t('common:text.error.clientid_incorrect_explanation')}
      </Typography>
      {status !== 503 &&
        <Typography variant="body1">
          {t('common:text.error.link_text')}
        </Typography>
      }

      {/* Si no funciona bé es pot provar
        https://docs.sentry.io/platforms/javascript/guides/nextjs/user-feedback/#integration
      */}
      {status >= 500 && !!lastEventId() && <>
        {t('common:text.error.sentry_code_message')} {lastEventId()}
      </>}
    </>}
    redirectComponent={
      <Stack
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
        sx={{marginTop: 3}}
        spacing={2}
      >
        <Button 
          variant="contained" 
          color="primary"
          startIcon={
            <Refresh />
          }
          onClick={authResetNavigate}
        >
          {t('common:text.error.refresh_button')}
        </Button>
      </Stack>
    }
  />
}

export default ErrorComponentPage;
